import logger from '../logger';
// @ts-ignore
import { AuthKeyIDPClient } from '../../../../api-sdk/javascript/src/index.js';
// @ts-ignore
import { createSMCryptoAdapter } from '../../../../api-sdk/javascript/src/adapters/sm-crypto.js';
const idpConfig = {
    idpId: 'authkey-identity-provider',
    idpURL: "/authkey-idp",
    appId: "authkey-meet",
    appSecret: "Vc5YpgOvBDdNfJiygcHbGQOrkii6qGLwoTtcbrtrTHsFa0qjPKdrN5RrSP33oAtW",
    timeout: 5 * 1000,
    publicParams: "",
};
const CHALLENGE_PATH = '/api/v1/challenge';
const AUTH_TOKEN_PATH = '/api/v1/authtoken';
const SERVER_PK_PATH = '/api/v1/getserverpk';
const AUTHKEY_CREDENTIAL_POINT_SIZE = 65;
const AUTHKEY_CREDENTIAL_STRUCT_SIZE = 97;
const AUTHKEY_SERVER_PUBLIC_KEY_SIZE = 33;
const AUTHKEY_AUTH_TOKEN_SIZE = 129;
const AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE = 130;
const AUTHKEY_MAX_USER_ID_LENGTH = 50;
const AUTHKEY_MAX_PASSWORD_LENGTH = 50;
const AUTHKEY_ERR_CRYPTO = -3;
const AUTHKEY_ERR_INVALID_POINT = -4;
const AUTHKEY_ERR_INVALID_PARAM = -6;
const AUTHKEY_SM2_ORDER = BigInt("0xFFFFFFFEFFFFFFFFFFFFFFFFFFFFFFFF7203DF6B21C6052B53BBF40939D54123");
const AUTHKEY_PEM_BEGIN = "-----BEGIN AuthKey-----";
const AUTHKEY_PEM_END = "-----END AuthKey-----";
const AUTHKEY_AKE_JS_FILE = "authkey_ake.js";
const AUTHKEY_AKE_WASM_FILE = "authkey_ake.wasm";
let authKeyAkeFactoryPromise = null;
function resolveAuthKeyAkeScriptURL() {
    const runtimeConfig = getRuntimeAuthKeyConfig();
    if (runtimeConfig.akeScriptURL) {
        return runtimeConfig.akeScriptURL;
    }
    if (runtimeConfig.akeBaseURL) {
        return `${trimTrailingSlash(runtimeConfig.akeBaseURL)}/${AUTHKEY_AKE_JS_FILE}`;
    }
    if (runtimeConfig.wasmBaseURL) {
        return `${trimTrailingSlash(runtimeConfig.wasmBaseURL)}/${AUTHKEY_AKE_JS_FILE}`;
    }
    return `/libs/${AUTHKEY_AKE_JS_FILE}`;
}
async function getAuthKeyAkeFactory() {
    if (!authKeyAkeFactoryPromise) {
        const scriptURL = resolveAuthKeyAkeScriptURL();
        // @ts-ignore The native TS target parses this web-only file with a module mode that disallows dynamic imports.
        authKeyAkeFactoryPromise = import(/* webpackIgnore: true */ scriptURL)
            .then(moduleValue => {
            const factory = moduleValue?.default;
            if (typeof factory !== 'function') {
                throw new TypeError(`AuthKey AKE module did not export a default factory: ${scriptURL}`);
            }
            return factory;
        })
            .catch(error => {
            authKeyAkeFactoryPromise = null;
            throw new Error(`Failed to load AuthKey AKE module: ${scriptURL}: ${error?.message || error}`);
        });
    }
    return authKeyAkeFactoryPromise;
}
function trimTrailingSlash(value) {
    return String(value || "").replace(/\/+$/, "");
}
function appendQueryParams(url, params) {
    const searchParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    const query = searchParams.toString();
    if (!query) {
        return url;
    }
    return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}
function getRuntimeAuthKeyConfig() {
    const runtimeRoot = typeof window !== 'undefined'
        ? window.config || {}
        : {};
    return runtimeRoot.authkey || runtimeRoot.letsauth || {};
}
function resolveAuthKeyWasmURL(path = AUTHKEY_AKE_WASM_FILE) {
    const runtimeConfig = getRuntimeAuthKeyConfig();
    if (runtimeConfig.wasmURL) {
        return runtimeConfig.wasmURL;
    }
    if (runtimeConfig.wasmBaseURL) {
        return `${trimTrailingSlash(runtimeConfig.wasmBaseURL)}/${path}`;
    }
    return `/libs/${path}`;
}
function getRuntimeIdpConfig() {
    const runtimeRoot = typeof window !== 'undefined'
        ? window.config || {}
        : {};
    const runtimeConfig = runtimeRoot.authkey || runtimeRoot.letsauth || {};
    const e2eeConfig = runtimeRoot.e2ee || {};
    return {
        ...idpConfig,
        ...runtimeConfig,
        idpURL: runtimeConfig.idpURL || runtimeConfig.baseURL
            || e2eeConfig.authKeyIdpURL || e2eeConfig.letsAuthIdpURL || idpConfig.idpURL,
        appId: runtimeConfig.appId || runtimeConfig.app_id || idpConfig.appId,
        appSecret: runtimeConfig.appSecret || runtimeConfig.app_secret || idpConfig.appSecret,
        publicParams: runtimeConfig.publicParams || runtimeConfig.public_params || runtimeConfig.publicParamsBase64
            || runtimeConfig.publicParamsHex || idpConfig.publicParams,
    };
}
function isHexString(value) {
    return value.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(value);
}
function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}
function bytesToBase64(bytes) {
    let binary = "";
    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
}
function base64ToBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
function parseAuthKeyPem(value) {
    const text = String(value || "").trim();
    if (!text.startsWith(AUTHKEY_PEM_BEGIN) || !text.includes(AUTHKEY_PEM_END)) {
        return null;
    }
    const base64Payload = text
        .replace(AUTHKEY_PEM_BEGIN, "")
        .replace(AUTHKEY_PEM_END, "")
        .replace(/\s+/g, "");
    const jsonBytes = base64ToBytes(base64Payload);
    const json = new TextDecoder().decode(jsonBytes);
    return JSON.parse(json);
}
function encodedToBytes(value, preferBase64 = false) {
    if (value instanceof Uint8Array) {
        return value;
    }
    if (Array.isArray(value)) {
        return new Uint8Array(value);
    }
    if (value && typeof value === "object") {
        return encodedToBytes(value.value || value.token || value.pk || value.credentialEnc || value.credential_enc
            || value.credential || value.publicParams || value.public_params, Boolean(value.value || value.token));
    }
    const text = String(value || "").trim();
    if (!text) {
        return new Uint8Array();
    }
    const authKeyPayload = parseAuthKeyPem(text);
    if (authKeyPayload) {
        return encodedToBytes(authKeyPayload);
    }
    try {
        return encodedToBytes(JSON.parse(text), preferBase64);
    }
    catch (e) {
        // Not JSON; fall through to hex/base64/plain text handling.
    }
    if (preferBase64) {
        try {
            return base64ToBytes(text);
        }
        catch (e) {
            if (isHexString(text)) {
                return hexToBytes(text);
            }
        }
    }
    else {
        if (isHexString(text)) {
            return hexToBytes(text);
        }
        try {
            return base64ToBytes(text);
        }
        catch (e) {
            return new TextEncoder().encode(text);
        }
    }
    return new TextEncoder().encode(text);
}
function authKeyUidFromUserId(userId) {
    const digestHex = createSMCryptoAdapter().hashHex(userId);
    const uidHex = (BigInt(`0x${digestHex}`) % AUTHKEY_SM2_ORDER)
        .toString(16)
        .padStart(64, '0');
    return hexToBytes(uidHex);
}
function buildAkeCredentialStruct(credential, userId) {
    const credentialBytes = encodedToBytes(credential);
    if (credentialBytes.length === AUTHKEY_CREDENTIAL_STRUCT_SIZE) {
        if (credentialBytes[32] !== 0x04) {
            throw new Error(`AuthKey credential has an invalid uncompressed SM2 point prefix: 0x${credentialBytes[32].toString(16).padStart(2, '0')}`);
        }
        return new Uint8Array(credentialBytes);
    }
    if (credentialBytes.length !== AUTHKEY_CREDENTIAL_POINT_SIZE) {
        throw new Error(`AuthKey credential must contain exactly ${AUTHKEY_CREDENTIAL_POINT_SIZE} raw point bytes; received ${credentialBytes.length}`);
    }
    if (credentialBytes[0] !== 0x04) {
        throw new Error(`AuthKey credential has an invalid uncompressed SM2 point prefix: 0x${credentialBytes[0].toString(16).padStart(2, '0')}`);
    }
    const credentialStruct = new Uint8Array(AUTHKEY_CREDENTIAL_STRUCT_SIZE);
    credentialStruct.set(authKeyUidFromUserId(userId), 0);
    credentialStruct.set(credentialBytes, 32);
    return credentialStruct;
}
function addAkeChallenge(token, challenge) {
    return token && typeof token === 'object'
        ? {
            ...token,
            ake_challenge: challenge,
        }
        : {
            ake_challenge: challenge,
            value: token,
        };
}
export default class AuthKeyService {
    sessionKey;
    localParticipantEmail;
    localParticipantCredential;
    localParticipantCredentialPassword;
    localAuthenticationStatus;
    participantsAuthenticationStatus;
    authenticatedBy;
    // Map for stroing the AKE state for each participant-moderator pair.
    AKESessionStateMap; // id(email) : {state, result}
    conferenceInstance;
    idpConfig;
    idpClient;
    serviceWASM;
    _initPromise;
    _isInitialized;
    constructor(conference) {
        this.conferenceInstance = conference || null;
        this.idpConfig = getRuntimeIdpConfig();
        this.idpClient = null;
        this.localAuthenticationStatus = false;
        this.participantsAuthenticationStatus = new Map();
        this.AKESessionStateMap = new Map();
        this.authenticatedBy = "0";
        // generate random session key
        const randomKeyBytes = window.crypto.getRandomValues(new Uint8Array(32));
        // convert Uint8Array to string
        this.sessionKey = Array.from(randomKeyBytes).map(b => b.toString(16).padStart(2, '0')).join('');
        // set local email & credential
        this.localParticipantEmail = "";
        this.localParticipantCredential = "";
        this.localParticipantCredentialPassword = "";
        this.serviceWASM = {
            AkeStart: null,
            AkeResponse: null,
            AkeFinal: null,
            AkeEnd: null,
        };
        this._initPromise = null;
        this._isInitialized = false;
    }
    isInitialized() {
        return this._isInitialized;
    }
    /**
     * Initialize the AuthKey service functions from wasm.
     */
    async initAuthKeyService() {
        if (this._isInitialized) {
            return;
        }
        if (!this._initPromise) {
            this._initPromise = this._initAuthKeyServiceInternal();
        }
        return this._initPromise;
    }
    async _initAuthKeyServiceInternal() {
        try {
            const wasmURL = resolveAuthKeyWasmURL();
            const moduleOptions = {
                // The generated module asks for authkeyAKE.wasm, while Jitsi deploys authkey_ake.wasm.
                locateFile: () => wasmURL,
            };
            const authKeyAkeFactory = await getAuthKeyAkeFactory();
            const authkey = await authKeyAkeFactory(moduleOptions);
            if (this.idpConfig.appSecret) {
                this.idpClient = new AuthKeyIDPClient({
                    appId: this.idpConfig.appId,
                    appSecret: this.idpConfig.appSecret,
                    baseURL: this.idpConfig.idpURL,
                    cryptoAdapter: createSMCryptoAdapter(),
                });
            }
            else {
                logger.warn('[authkey] Missing appSecret. Configure window.config.authkey.appSecret before using AuthKey IdP APIs.');
            }
            /****************** Hybrid Post-Quantum AKE Interfaces  ******************/
            let serverPublicKeyValue = "";
            const allocBytes = (bytes) => {
                const ptr = authkey._malloc(bytes.length);
                authkey.HEAPU8.set(bytes, ptr);
                return ptr;
            };
            const allocStruct = (value, size, label, preferBase64 = false) => {
                const bytes = encodedToBytes(value, preferBase64);
                if (bytes.length !== size) {
                    throw new Error(`${label} must contain exactly ${size} bytes; received ${bytes.length}`);
                }
                return allocBytes(bytes);
            };
            const getServerPublicKey = async () => {
                if (serverPublicKeyValue) {
                    return serverPublicKeyValue;
                }
                const serverPkResult = await this.requestServerPublicParams();
                if (!serverPkResult.success && !this.idpConfig.publicParams) {
                    throw new Error(serverPkResult.message || "Failed to load AuthKey server public key");
                }
                serverPublicKeyValue = serverPkResult.success ? serverPkResult.data.pk : this.idpConfig.publicParams;
                return serverPublicKeyValue;
            };
            const wasm_PQX_AKE_leader_init = authkey.cwrap("pqkx_leader_init", "number", ["string", "string", "string", "number", "string"]);
            const wasm_PQX_generate_auth_request = authkey.cwrap("pqkx_generate_auth_request", "number", [
                "number",
                "string",
                "string",
                "string",
                "number"
            ]);
            const diagnoseAuthRequestFailure = (credentialPtr, userId, password, nonce) => {
                const tokenPtr = authkey._malloc(AUTHKEY_AUTH_TOKEN_SIZE);
                if (!tokenPtr) {
                    return null;
                }
                try {
                    authkey.HEAPU8.fill(0, tokenPtr, tokenPtr + AUTHKEY_AUTH_TOKEN_SIZE);
                    return wasm_PQX_generate_auth_request(credentialPtr, userId, password, nonce, tokenPtr);
                }
                finally {
                    authkey.HEAPU8.fill(0, tokenPtr, tokenPtr + AUTHKEY_AUTH_TOKEN_SIZE);
                    authkey._free(tokenPtr);
                }
            };
            const getLeaderInitFailureMessage = (authRequestRc) => {
                switch (authRequestRc) {
                    case AUTHKEY_ERR_INVALID_POINT:
                        return "AuthKey credential_enc contains an invalid SM2 point; re-export the 65-byte encrypted credential";
                    case AUTHKEY_ERR_INVALID_PARAM:
                        return `AuthKey user ID and password must not exceed ${AUTHKEY_MAX_USER_ID_LENGTH} characters`;
                    case AUTHKEY_ERR_CRYPTO:
                        return "AuthKey failed to generate the authentication request due to a cryptographic error";
                    case 0:
                        return "AuthKey leader key generation or state allocation failed";
                    default:
                        return authRequestRc === null
                            ? "Failed to initialize AuthKey leader AKE state (diagnostic allocation failed)"
                            : `Failed to initialize AuthKey leader AKE state (auth request error ${authRequestRc})`;
                }
            };
            const PQX_AKE_leaderInit = (leaderId, userId, password, credential, nonce) => {
                if (leaderId.length > AUTHKEY_MAX_USER_ID_LENGTH) {
                    throw new Error(`AuthKey user ID must not exceed ${AUTHKEY_MAX_USER_ID_LENGTH} characters; received ${leaderId.length}`);
                }
                if (password.length > AUTHKEY_MAX_PASSWORD_LENGTH) {
                    throw new Error(`AuthKey password must not exceed ${AUTHKEY_MAX_PASSWORD_LENGTH} characters; received ${password.length}`);
                }
                const credentialStruct = buildAkeCredentialStruct(credential, leaderId);
                const credentialPtr = allocBytes(credentialStruct);
                let statePtr = 0;
                let authRequestRc = null;
                try {
                    statePtr = wasm_PQX_AKE_leader_init(leaderId, userId, password, credentialPtr, nonce);
                    if (!statePtr) {
                        authRequestRc = diagnoseAuthRequestFailure(credentialPtr, leaderId, password, nonce);
                    }
                }
                finally {
                    authkey.HEAPU8.fill(0, credentialPtr, credentialPtr + credentialStruct.length);
                    authkey._free(credentialPtr);
                    credentialStruct.fill(0);
                }
                if (!statePtr) {
                    throw new Error(getLeaderInitFailureMessage(authRequestRc));
                }
                return this.buildPQXState(authkey, statePtr, leaderId, userId);
            };
            const wasm_PQX_AKE_leader_send = authkey.cwrap("pqkx_leader_init_send", "number", [
                "number",
                "number",
                "number"
            ]);
            const PQX_AKE_leaderInitSend = (transformedToken, serverPublicKey, state) => {
                const tokenPtr = allocStruct(transformedToken?.value || transformedToken?.token || transformedToken, AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE, "AuthKey transformed token", true);
                const serverPublicKeyPtr = allocStruct(serverPublicKey, AUTHKEY_SERVER_PUBLIC_KEY_SIZE, "AuthKey server public key");
                let rc = -1;
                try {
                    rc = wasm_PQX_AKE_leader_send(tokenPtr, serverPublicKeyPtr, this.getPQXStatePtr(state));
                }
                finally {
                    authkey._free(tokenPtr);
                    authkey._free(serverPublicKeyPtr);
                }
                if (rc !== 0) {
                    throw new Error(`AuthKey leader init send failed: ${rc}`);
                }
                return {
                    ...this.buildPQXState(authkey, this.getPQXStatePtr(state), state.leader_id, state.user_id),
                    transformed_token: transformedToken,
                };
            };
            const wasm_PQX_AKE_user_resp = authkey.cwrap("pqkx_user_response", "number", [
                "string",
                "string",
                "string",
                "number",
                "string",
                "string",
                "string",
                "number",
                "number"
            ]);
            const PQX_AKE_userResponse = (leaderId, userId, password, credential, nonce, leaderEcdhPublicKey, leaderKemPk, serverPublicKey, leaderToken) => {
                const credentialStruct = buildAkeCredentialStruct(credential, userId);
                const credentialPtr = allocBytes(credentialStruct);
                const serverPublicKeyPtr = allocStruct(serverPublicKey, AUTHKEY_SERVER_PUBLIC_KEY_SIZE, "AuthKey server public key");
                const leaderTokenPtr = allocStruct(leaderToken?.value || leaderToken?.token || leaderToken, AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE, "AuthKey transformed token", true);
                let statePtr = 0;
                try {
                    statePtr = wasm_PQX_AKE_user_resp(leaderId, userId, password, credentialPtr, nonce, leaderEcdhPublicKey, leaderKemPk, serverPublicKeyPtr, leaderTokenPtr);
                }
                finally {
                    authkey.HEAPU8.fill(0, credentialPtr, credentialPtr + credentialStruct.length);
                    authkey._free(credentialPtr);
                    credentialStruct.fill(0);
                    authkey._free(serverPublicKeyPtr);
                    authkey._free(leaderTokenPtr);
                }
                if (!statePtr) {
                    throw new Error("Failed to initialize AuthKey user AKE state");
                }
                return this.buildPQXState(authkey, statePtr, leaderId, userId);
            };
            const wasm_PQX_AKE_user_resp_send = authkey.cwrap("pqkx_user_response_send", "number", ["number", "number", "number"]);
            const PQX_AKE_userResponseSend = (token, serverPublicKey, state) => {
                const tokenPtr = allocStruct(token?.value || token?.token || token, AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE, "AuthKey transformed token", true);
                const serverPublicKeyPtr = allocStruct(serverPublicKey, AUTHKEY_SERVER_PUBLIC_KEY_SIZE, "AuthKey server public key");
                let rc = -1;
                try {
                    rc = wasm_PQX_AKE_user_resp_send(tokenPtr, serverPublicKeyPtr, this.getPQXStatePtr(state));
                }
                finally {
                    authkey._free(tokenPtr);
                    authkey._free(serverPublicKeyPtr);
                }
                if (rc !== 0) {
                    throw new Error(`AuthKey user response send failed: ${rc}`);
                }
                return {
                    ...this.buildPQXState(authkey, this.getPQXStatePtr(state), state.leader_id, state.user_id),
                    transformed_token: token,
                };
            };
            const wasm_PQX_AKE_leader_finish = authkey.cwrap("pqkx_leader_finish", "number", [
                "string",
                "string",
                "string",
                "string",
                "number",
                "number",
                "number"
            ]);
            const PQX_AKE_leaderFinish = (sessionKey, userEcdhPublicKey, userKemPk, userKemCt, serverPublicKey, token, state) => {
                const serverPublicKeyPtr = allocStruct(serverPublicKey, AUTHKEY_SERVER_PUBLIC_KEY_SIZE, "AuthKey server public key");
                const tokenPtr = allocStruct(token?.value || token?.token || token, AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE, "AuthKey transformed token", true);
                let packagePtr = 0;
                try {
                    packagePtr = wasm_PQX_AKE_leader_finish(sessionKey, userEcdhPublicKey, userKemPk, userKemCt, serverPublicKeyPtr, tokenPtr, this.getPQXStatePtr(state));
                }
                finally {
                    authkey._free(serverPublicKeyPtr);
                    authkey._free(tokenPtr);
                }
                if (!packagePtr) {
                    throw new Error("Failed to finish AuthKey leader AKE");
                }
                try {
                    return {
                        kem_ct: authkey.ccall("pqx_session_key_package_get_kem_ct", "string", ["number"], [packagePtr]),
                        key_ct: authkey.ccall("pqx_session_key_package_get_key_ct", "string", ["number"], [packagePtr]),
                    };
                }
                finally {
                    authkey._pqkx_session_key_package_free(packagePtr);
                }
            };
            const wasm_PQX_AKE_user_finish = authkey.cwrap("pqkx_user_finish", "string", [
                "string",
                "string",
                "number",
            ]);
            const PQX_AKE_userFinish = (sessionKeyCt, leaderKemCt, state) => {
                return wasm_PQX_AKE_user_finish(sessionKeyCt, leaderKemCt, this.getPQXStatePtr(state));
            };
            // build the AKE functions
            this.serviceWASM.AkeStart = async (leaderId, userId, credential, password) => {
                // request a nonce from the IdP server
                const challengeResult = await this.requestChallenge(leaderId);
                if (!challengeResult.success) {
                    return {
                        data: {},
                        message: challengeResult.message,
                        success: false,
                    };
                }
                else {
                    try {
                        const nonce = challengeResult.data.nonce;
                        let leaderState = await PQX_AKE_leaderInit(leaderId, userId, password, credential, nonce);
                        const transformedTokenResult = await this.requestTransformedToken(challengeResult.data.authId, leaderId, leaderState.auth_token);
                        if (!transformedTokenResult.success) {
                            return {
                                data: {},
                                message: transformedTokenResult.message,
                                success: false,
                            };
                        }
                        const transformedToken = transformedTokenResult.data;
                        leaderState = PQX_AKE_leaderInitSend(transformedToken, await getServerPublicKey(), leaderState);
                        leaderState.ake_challenge = nonce;
                        leaderState.transformed_token = addAkeChallenge(transformedToken, nonce);
                        return {
                            data: leaderState,
                            message: "AKE init success",
                            success: true,
                        };
                    }
                    catch (e) {
                        const error = e;
                        logger.error("[authkey] Generate AKE init state failed: ", error);
                        return {
                            data: {},
                            message: error?.message || "Failed to generate AuthKey AKE init state",
                            success: false,
                        };
                    }
                }
            };
            this.serviceWASM.AkeResponse = async (leaderId, userId, credential, password, leaderPublicKey, leaderKemPublicKey, leaderToken) => {
                // request a nonce from the IdP server
                const challengeResult = await this.requestChallenge(userId);
                if (!challengeResult.success) {
                    return {
                        data: {},
                        message: challengeResult.message,
                        success: false,
                    };
                }
                else {
                    try {
                        const nonce = challengeResult.data.nonce;
                        let userState = PQX_AKE_userResponse(leaderId, userId, password, credential, nonce, leaderPublicKey, leaderKemPublicKey, await getServerPublicKey(), leaderToken);
                        const transformedTokenResult = await this.requestTransformedToken(challengeResult.data.authId, userId, userState.auth_token);
                        if (!transformedTokenResult.success) {
                            return {
                                data: {},
                                message: transformedTokenResult.message,
                                success: false,
                            };
                        }
                        const transformedToken = transformedTokenResult.data;
                        userState = PQX_AKE_userResponseSend(transformedToken, await getServerPublicKey(), userState);
                        userState.ake_challenge = nonce;
                        userState.transformed_token = addAkeChallenge(transformedToken, nonce);
                        return {
                            data: userState,
                            message: "AKE user response success",
                            success: true,
                        };
                    }
                    catch (e) {
                        const error = e;
                        logger.error("[authkey] Generate AKE response state failed: ", error);
                        return {
                            data: {},
                            message: error?.message || "Failed to generate AuthKey AKE response state",
                            success: false,
                        };
                    }
                }
            };
            this.serviceWASM.AkeFinal = async (userId, userPublicKey, KemPublicKey, KemCiphertext, userToken, oldState) => {
                try {
                    const encryptedSessionKey = PQX_AKE_leaderFinish(this.sessionKey, userPublicKey, KemPublicKey, KemCiphertext, await getServerPublicKey(), userToken, oldState);
                    return {
                        data: encryptedSessionKey,
                        message: "Leader finished AKE.",
                        success: true,
                    };
                }
                catch (e) {
                    const error = e;
                    logger.error("[authkey] Finish AKE as leader failed: ", error);
                    return {
                        data: {},
                        message: error?.message || "Failed to finish AuthKey leader AKE",
                        success: false,
                    };
                }
            };
            this.serviceWASM.AkeEnd = (encryptedSessionKey, leaderKemCiphertext, oldState) => {
                try {
                    const sessionKey = PQX_AKE_userFinish(encryptedSessionKey, leaderKemCiphertext, oldState);
                    this.sessionKey = sessionKey;
                    return {
                        data: sessionKey,
                        message: "User finished AKE.",
                        success: true,
                    };
                }
                catch (e) {
                    const error = e;
                    logger.error("[authkey] Finish AKE as user failed: ", error);
                    return {
                        data: {},
                        message: error?.message || "Failed to finish AuthKey user AKE",
                        success: false,
                    };
                }
            };
            this._isInitialized = true;
        }
        catch (error) {
            this._initPromise = null;
            logger.error('[authkey] initAuthKeyService failed', error);
            throw error;
        }
    }
    buildPQXState(authkey, statePtr, leaderId, userId) {
        const authTokenPtr = authkey.ccall("pqkx_state_get_auth_token", "number", ["number"], [statePtr]);
        const transformedTokenPtr = authkey.ccall("pqkx_state_get_transformed_token", "number", ["number"], [statePtr]);
        const structToBase64 = (ptr, size) => ptr
            ? bytesToBase64(authkey.HEAPU8.slice(ptr, ptr + size))
            : "";
        return {
            __pqkx_state_ptr: statePtr,
            auth_token: structToBase64(authTokenPtr, AUTHKEY_AUTH_TOKEN_SIZE),
            ake_challenge: "",
            ecdh_pk: authkey.ccall("pqkx_state_get_ecdh_pk", "string", ["number"], [statePtr]),
            leader_id: leaderId,
            pqkem_ct: authkey.ccall("pqkx_state_get_pqkem_ct", "string", ["number"], [statePtr]),
            pqkem_pk: authkey.ccall("pqkx_state_get_pqkem_pk", "string", ["number"], [statePtr]),
            transformed_token: transformedTokenPtr ? {
                ake_challenge: "",
                value: structToBase64(transformedTokenPtr, AUTHKEY_PUBLIC_AUTH_TOKEN_SIZE),
            } : null,
            user_id: userId,
        };
    }
    getPQXStatePtr(state) {
        const statePtr = state?.__pqkx_state_ptr;
        if (!statePtr) {
            throw new Error("AuthKey AKE state pointer is missing");
        }
        return statePtr;
    }
    async signedRequest(method, path, payload) {
        if (!this.idpClient) {
            return {
                data: null,
                message: "AuthKey IdP client is not configured",
                success: false,
            };
        }
        const body = method === "GET" ? "" : JSON.stringify(payload);
        const headers = this.idpClient.buildHeaders({
            method,
            path,
            body,
            headers: {
                "Content-Type": "application/json",
            },
        });
        const baseURL = `${trimTrailingSlash(this.idpConfig.idpURL)}${path}`;
        const requestURL = method === "GET" ? appendQueryParams(baseURL, payload) : baseURL;
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutMs = Number(this.idpConfig.timeout || 0);
        const timeout = controller && timeoutMs > 0
            ? window.setTimeout(() => controller.abort(), timeoutMs)
            : null;
        try {
            const response = await fetch(requestURL, {
                body: method === "GET" ? undefined : body,
                credentials: 'same-origin',
                headers,
                method,
                signal: controller?.signal,
            });
            const responseText = await response.text();
            let responsePayload = null;
            if (responseText) {
                try {
                    responsePayload = JSON.parse(responseText);
                }
                catch (error) {
                    logger.error(`AuthKey IdP returned non-JSON response (${path}): ${responseText}`);
                    return {
                        data: null,
                        message: `AuthKey IdP ${path} returned non-JSON response`,
                        success: false,
                    };
                }
            }
            if (!response.ok) {
                logger.error(`AuthKey IdP request failed (${path}): ${response.status} ${response.statusText}`, responsePayload);
                return {
                    data: responsePayload,
                    message: responsePayload?.message || `AuthKey IdP ${path} returned ${response.status}`,
                    success: false,
                };
            }
            if (responsePayload?.code && responsePayload.code !== "OK") {
                return {
                    data: responsePayload,
                    message: responsePayload.message || `AuthKey IdP ${path} returned ${responsePayload.code}`,
                    success: false,
                };
            }
            return {
                data: responsePayload,
                message: "success",
                success: true,
            };
        }
        catch (error) {
            const message = error?.name === 'AbortError'
                ? `AuthKey IdP request timed out (${path})`
                : `Failed to request AuthKey IdP ${path}`;
            logger.error(message, error);
            return {
                data: null,
                message,
                success: false,
            };
        }
        finally {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        }
    }
    signedPost(path, payload) {
        return this.signedRequest("POST", path, payload);
    }
    signedGet(path, payload) {
        return this.signedRequest("GET", path, payload);
    }
    async requestChallenge(uid) {
        const result = await this.signedPost(CHALLENGE_PATH, {
            uid,
            app_id: this.idpConfig.appId,
        });
        const nonce = result.data?.data?.nonce;
        if (!result.success || !nonce) {
            return {
                data: {},
                message: result.message || "Error requesting challenge from IdP server",
                success: false,
            };
        }
        return {
            data: {
                authId: result.data.data.auth_id || uid,
                nonce,
            },
            message: "challenge success",
            success: true,
        };
    }
    async requestServerPublicParams() {
        const result = await this.signedGet(SERVER_PK_PATH, {
            app_id: this.idpConfig.appId,
        });
        const pk = result.data?.data?.pk || result.data?.pk;
        if (!result.success || !pk) {
            return {
                data: {},
                message: result.message || "Error requesting server public params from IdP server",
                success: false,
            };
        }
        return {
            data: {
                pk,
            },
            message: "server public params success",
            success: true,
        };
    }
    async requestTransformedToken(authId, uid, authenticationToken) {
        const result = await this.signedPost(AUTH_TOKEN_PATH, {
            auth_id: authId,
            uid,
            app_id: this.idpConfig.appId,
            token: typeof authenticationToken === "string"
                ? authenticationToken
                : JSON.stringify(authenticationToken),
        });
        const responseData = result.data?.data;
        const transformedToken = responseData?.token;
        if (!result.success || responseData?.result === false || !transformedToken) {
            return {
                data: {},
                message: result.message || "Failed to authenticate with IdP server",
                success: false,
            };
        }
        return {
            data: transformedToken,
            message: "authtoken success",
            success: true,
        };
    }
    /**
     * Set local session key (string)
     * @param newSessionKey
     */
    setSessionKey(newSessionKey) {
        this.sessionKey = newSessionKey;
    }
    /**
     * Get current local session key (string)
     * @returns local session key (string)
     */
    getSessionKey() {
        return this.sessionKey;
    }
    /**
     * Get current local session key (bytes), used for set encryption key.
     * @returns local session key (bytes)
     */
    getSessionKeyBytes() {
        const sessionKey = String(this.sessionKey || "").trim();
        if (!sessionKey) {
            return new Uint8Array();
        }
        if (isHexString(sessionKey)) {
            return hexToBytes(sessionKey);
        }
        try {
            return base64ToBytes(sessionKey);
        }
        catch (e) {
            return new TextEncoder().encode(sessionKey);
        }
    }
    /**
     * Update local session key (string)
     */
    updateSessionKey() {
        const newSessionKeyBytes = window.crypto.getRandomValues(new Uint8Array(32));
        const newSessionKey = Array.from(newSessionKeyBytes).map(b => b.toString(16).padStart(2, '0')).join('');
        this.sessionKey = newSessionKey;
    }
    /**
     * Update local authentication state (true for passed authentication, else false)
     * @param newStatus
     */
    setLocalAuthenticationStatus(newStatus) {
        this.localAuthenticationStatus = newStatus;
    }
    /**
     * Get local authentication state
     * @returns local authentication state (true for passed authentication, else false)
     */
    getLocalAuthenticationStatus() {
        return this.localAuthenticationStatus;
    }
    /**
     * Moderator sets participant authentication state (true for passed authentication, else false)
     * @param participantId
     * @param newStatus
     */
    setParticipantAuthenticationStatus(participantId, newStatus) {
        this.participantsAuthenticationStatus.set(participantId, newStatus);
    }
    /**
     * Moderator gets the authentication result of a participant
     * @param participantId
     * @returns
     */
    getParticipantAuthenticationStatus(participantId) {
        return this.participantsAuthenticationStatus.get(participantId);
    }
    setAuthenticatedBy(moderatorId) {
        this.authenticatedBy = moderatorId;
    }
    getAuthenticatedBy() {
        return this.authenticatedBy;
    }
    /**
     * Set local credential infomation for authentication
     * @param email
     * @param credential
     * @param password
     */
    setLocalCredentialInfo(email, credential, password) {
        this.localParticipantEmail = email;
        this.localParticipantCredential = credential;
        this.localParticipantCredentialPassword = password;
    }
    /**
     * Get current credential information.
     * @returns local credential infomation for authentication
     */
    getLocalCredentialInfo() {
        return {
            email: this.localParticipantEmail,
            credential: this.localParticipantCredential,
            credentialPassword: this.localParticipantCredentialPassword,
        };
    }
    setAKESessionState(pId, state) {
        this.AKESessionStateMap.set(pId, state);
    }
    getAKESessionState(pId) {
        return this.AKESessionStateMap.get(pId);
    }
    removeAKESessionState(pId) {
        this.AKESessionStateMap.delete(pId);
    }
}
